import { useAtomValue } from 'jotai';
import { useTracker } from '@archipro-website/tracker';
import { Image } from '@rocco/components/image';
import { uiContextAtomViewTransitionsEnabled } from '@rocco/states/uiContextAtomViewTransitions';
import {
    ARTICLE_HERO_TRANSITION_NAME,
    recordTileMorphStart,
    startsPlainNavigation,
    usePrefersReducedMotion,
    useTileHeroViewTransitionStyle,
} from '@rocco/ui/tile-shared/hooks/use-tile-hero-view-transition';
import {
    EDITORIAL_GRADE_MEDIA,
    EditorialGradeWash,
} from '@rocco/ui/tile-article/editorial-grade';
import { RoccoLink } from '@rocco/components/link';
import { cn } from '@rocco/utils/cn';
import { detailCta } from '~/ui-rocco/pages/detail-shared';
import { trackTileMediaDetailClick } from '~/modules/tracking/util/trackTileMedia';
import { DIRECTORY_GUTTER_X } from './EditorialArticleGrid';
import type { ArticleTileViewModel } from '@rocco/ui/tile-article/view-models/article';

interface EditorialFeatureRowProps {
    article: ArticleTileViewModel;
    className?: string;
}

/**
 * One article given a whole row, after a full turn of the grid's rhythm.
 *
 * A picture on the left and the copy beside it, rather than another card: the
 * grid states what an article is in three lines, and this has room to say why
 * it is worth the reader's time. It sits on the page's own panel so the row
 * reads as a pause in the listing rather than as a fourth grid variant.
 */
export const EditorialFeatureRow = ({
    article,
    className,
}: EditorialFeatureRowProps) => {
    // The same opt-in the grid's tiles make: the picture flies into the
    // article's opening picture, behind the feature flag alone.
    const viewTransitionsFlagOn = useAtomValue(
        uiContextAtomViewTransitionsEnabled,
    );
    const prefersReducedMotion = usePrefersReducedMotion();
    const viewTransitionsEnabled =
        viewTransitionsFlagOn && !prefersReducedMotion;
    const heroStyle = useTileHeroViewTransitionStyle(
        viewTransitionsEnabled ? article.link : undefined,
        ARTICLE_HERO_TRANSITION_NAME,
    );
    const tracker = useTracker();
    // Either way into the article is the click the grid's tiles count.
    const trackClick = () =>
        trackTileMediaDetailClick(tracker, {
            itemID: article.id,
            itemType: 'ArticleTile',
            itemName: article.title,
        });

    if (!article.image?.src) return null;

    const label = article.categoryLabel || article.label;

    return (
        <section aria-label={article.title} className={cn('w-full', className)}>
            {/* On the page's own paper, not a band of its own: the row is
                the picture and its copy, and a panel behind it made it read
                as an advertisement between the cards. Its horizontal padding
                matches the grid's margin, so the picture still starts where
                the cards above it do. */}
            <div
                className={cn(
                    // 172px between the picture and the copy at 1920: the
                    // copy stands off the picture rather than against it.
                    'flex items-center gap-[10.75rem] py-[3.75rem]',
                    'max-md:flex-col max-md:items-stretch max-md:gap-[2rem] max-md:py-[2.5rem]',
                    DIRECTORY_GUTTER_X,
                )}
            >
                {/* Square, as the reference sets it: the row is wide enough
                    that a landscape crop would run the copy off the measure. */}
                <RoccoLink
                    to={article.link}
                    prefetch="intent"
                    viewTransition={viewTransitionsEnabled}
                    style={heroStyle}
                    onClick={(event) => {
                        trackClick();
                        if (
                            !viewTransitionsEnabled ||
                            !startsPlainNavigation(event)
                        ) {
                            return;
                        }
                        recordTileMorphStart(
                            ARTICLE_HERO_TRANSITION_NAME,
                            article.link,
                            event.currentTarget.querySelector('img')
                                ?.currentSrc,
                        );
                    }}
                    className={cn(
                        'relative aspect-square w-[42%] shrink-0 overflow-hidden rounded-tile max-md:w-full',
                        EDITORIAL_GRADE_MEDIA,
                    )}
                >
                    <Image
                        src={article.image.src}
                        alt={article.image.alt ?? article.title}
                        fill={true}
                        fit="cover"
                        sizes="(min-width: 768px) 42vw, 100vw"
                        className="absolute inset-0"
                    />
                    <EditorialGradeWash />
                </RoccoLink>

                <div className="flex min-w-0 flex-col md:max-w-[34rem]">
                    {label ? (
                        <p className="m-0 font-sans text-[0.875rem] leading-[1.25rem] tracking-[0.08em] text-[#6e6a62] uppercase">
                            {label}
                        </p>
                    ) : null}

                    <h3 className="m-0 mt-[1.25rem] font-gestura text-[2.125rem] leading-[1.1] font-normal tracking-[-0.03em] text-[#1f1f21] md:text-[3rem]">
                        {article.title}
                    </h3>

                    {article.introText ? (
                        <p className="m-0 mt-[1.5rem] line-clamp-4 font-sans text-[0.9375rem] leading-[1.5rem] font-normal text-[#1f1f21]">
                            {article.introText}
                        </p>
                    ) : null}

                    <RoccoLink
                        to={article.link}
                        prefetch="intent"
                        onClick={trackClick}
                        // The hairline pill, not the filled one: the whole row
                        // already links to the article, so a primary action
                        // beside it would read as a second, louder thing to do.
                        // `w-fit`, or the column it sits in stretches it to
                        // the full measure of the copy beside the picture.
                        className={detailCta(
                            'outlined-light',
                            'mt-[2rem] w-fit',
                        )}
                    >
                        {article.hasVideo ? 'Watch now' : 'Read now'}
                    </RoccoLink>
                </div>
            </div>
        </section>
    );
};
